import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col, Typography, Empty, Button, Alert } from 'antd';
import { LineChartOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useDashboardNavigation } from '../../contexts/DashboardNavigationContext';
import { useUser } from '../../contexts/UserContext';
import { ProductProjectionsComponent } from '../../components/Dashboard/ReportingComponents';
import { ProjectionSimulatePanel } from '../../components/HierarchicalDashboard/ProjectionSimulatePanel';
import { resolveWorkspaceCompanyId } from '../../utils/resolveWorkspaceCompanyId';
import styles from './ProjectionsDashboard.module.css';

const { Title, Text } = Typography;

// ============================================================================
// PROJECTIONS DASHBOARD
// ============================================================================

export const ProjectionsDashboard: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { currentLocation, goBack } = useDashboardNavigation();
  const { user, selectedCompany, availableCompanies: companiesList = [] } = useUser();
  const workspaceCompanyId = useMemo(
    () => resolveWorkspaceCompanyId(user?.companyId, selectedCompany?.id, companiesList),
    [user?.companyId, selectedCompany?.id, companiesList]
  );

  if (!productId) {
    return (
      <div className={styles.emptyContainer}>
        <Empty
          description="No product selected"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={goBack}>
            Go Back
          </Button>
        </Empty>
      </div>
    );
  }

  const productName = currentLocation.productName || 'Product';

  return (
    <div className={styles.dashboard}>
      {/* Dashboard Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <LineChartOutlined className={styles.headerIcon} />
          <div>
            <Title level={2} className={styles.title}>
              Projections - {productName}
            </Title>
            <Text className={styles.subtitle}>
              Financial forecasts, assumptions, and projection analysis
            </Text>
          </div>
        </div>
      </div>

      {/* Main Projections Component */}
      <div className={styles.mainContent}>
        <ProductProjectionsComponent
          artifactId={productId}
        />
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <ProjectionSimulatePanel companyId={workspaceCompanyId} />
        </Col>
      </Row>

      {/* TODO(ISSUE-218): Replace with full assumption-manager UX when WP-010 productization is scheduled. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Assumption manager"
        description="Interactive growth and resource assumption controls are not part of the MVP surface. Use the supply projection simulate panel above for deterministic scenarios; full assumption productization is tracked post-MVP (WP-010). See ISSUE-218."
      />
    </div>
  );
};

export default ProjectionsDashboard;
